import { useEffect, useRef, useState } from 'react';
import type { AccessGroupSize, AccessUse, CreateStudioResponse, StudioSummary } from '../../../shared/protocol.ts';
import { account, isEmail } from '../lib/account.ts';
import { api } from '../lib/api.ts';
import { formatBytes, formatDuration } from '../lib/emitter.ts';
import { ACCESS_SIZES, ACCESS_USES, AppBar, ConfirmDialog, CopyField, Icon, Menu, roleLabel, useInterval, useObservable, useTitle } from './common.tsx';
import { LocalCopies } from './LocalCopies.tsx';
import { SignInCode } from './SignInCode.tsx';
import { UploadsPanel } from './UploadsPanel.tsx';

const FEATURES = [
  { title: 'Separate local tracks', text: 'Each camera, mic, screen or app window becomes its own file: H.264 MP4 video and lossless WAV audio.' },
  { title: 'One shared timeline', text: 'Clocks sync to the server, so every file carries its exact start time. Late joiners and mid-take shares land where they happened.' },
  { title: 'Instant replay check', text: 'Replay the last five seconds of every source from disk, with automatic checks for silence, black or frozen video.' },
  { title: 'Uploads that protect the call', text: 'Uploads resume after crashes, verify every chunk and back off the moment the live call needs bandwidth.' },
];

/** The illustration beside the welcome: one take's local tracks on a shared timeline (percent of the window shown). */
const DEMO_TRACKS = [
  { who: 'Duncan', what: 'Camera', from: 0, to: 100 },
  { who: 'Duncan', what: 'Mic', from: 0, to: 100 },
  { who: 'Priya', what: 'Camera', from: 0, to: 100 },
  { who: 'Priya', what: 'Mic', from: 0, to: 100 },
  { who: 'Priya', what: 'Screen', from: 38, to: 82 },
  { who: 'Tom', what: 'Camera', from: 22, to: 100 },
  { who: 'Tom', what: 'Mic', from: 22, to: 100 },
];

/** Links here (e.g. wordstow.com/#register) lead to registering interest in creating studios. */
const REGISTER = '#register';

/** Home: a welcome and email sign-in when signed out; your studios and recordings when signed in. */
export function Landing() {
  useObservable(account);
  return (
    <div className="page">
      <AppBar />
      {account.signedIn ? <Dashboard /> : <Welcome />}
      <footer className="footer">
        <span>Recordings stay on each computer until they’re safely uploaded.</span>
        <span>Works best in current Chrome or Edge; Safari supported.</span>
      </footer>
    </div>
  );
}

// ---------------------------------------------------------------------------

function Welcome() {
  useTitle('');
  const [inviteOnly, setInviteOnly] = useState(false);
  const [interest, setInterest] = useState(location.hash === REGISTER);
  useEffect(() => void api.config().then((c) => setInviteOnly(c.inviteOnly)).catch(() => {}), []);
  useEffect(() => {
    const onHash = () => setInterest(location.hash === REGISTER);
    addEventListener('hashchange', onHash);
    return () => removeEventListener('hashchange', onHash);
  }, []);
  return (
    <main className="landing">
      <section className="hero">
        <div className="hero-text">
          <span className="eyebrow rec-eyebrow">
            <span className="dot" /> Remote recording studio
          </span>
          <h1>Record everyone locally. In sync. At full quality.</h1>
          <p className="lede">
            Talk over a light live call while every participant’s camera, mic and screen are recorded on their own computer and
            uploaded in the background. The call can stutter; the recordings won’t.
          </p>
          <SignInCard focus={interest} />
          {inviteOnly && (
            <div className="prerelease">
              <span className="pill accent">Pre-release</span>
              {interest ? (
                <span>Continue with your email above. Next you’ll tell us what you’d like to record, and we’ll email you when you can create studios.</span>
              ) : (
                <>
                  <span>We’re looking for test users to create studios.</span>
                  <a href={REGISTER}>Register interest</a>
                </>
              )}
            </div>
          )}
        </div>
        <TakeIllustration />
      </section>
      <UploadsPanel />
      <LocalCopies />
      <section className="features">
        {FEATURES.map((f, i) => (
          <div key={f.title} className="feature">
            <span className="feature-n">{String(i + 1).padStart(2, '0')}</span>
            <h3>{f.title}</h3>
            <p>{f.text}</p>
          </div>
        ))}
      </section>
    </main>
  );
}

function TakeIllustration() {
  return (
    <div className="take-demo" aria-hidden>
      <div className="take-demo-head">
        <span className="rec-word">
          <span className="dot" /> REC
        </span>
        <span className="take-demo-time">00:41:12</span>
        <span className="grow" />
        <span className="muted small">Take 3 · 7 local tracks</span>
      </div>
      <div className="take-demo-grid">
        <span className="take-demo-label eyebrow">Track</span>
        <span className="take-demo-scale">
          <span>40:00</span>
          <span>40:30</span>
          <span>41:00</span>
          <span>41:30</span>
        </span>
        {DEMO_TRACKS.map((t) => (
          <div key={t.who + t.what} className="take-demo-row">
            <span className="take-demo-label">
              {t.who} <span className="faint">{t.what}</span>
            </span>
            <span className="take-demo-lane">
              <i className={t.what === 'Mic' ? 'audio' : t.what === 'Screen' ? 'screen' : ''} style={{ left: `${t.from}%`, width: `${t.to - t.from}%` }} />
            </span>
          </div>
        ))}
      </div>
      <div className="take-demo-foot">
        <span>Every file carries its exact start time</span>
        <span className="mono">sync ±0.1 ms</span>
      </div>
    </div>
  );
}

function SignInCard({ focus }: { focus?: boolean }) {
  const emailInput = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (focus) emailInput.current?.focus();
  }, [focus]);
  const [email, setEmail] = useState('');
  const [name, setName] = useState(localStorage.getItem('takehouse.name') ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [pending, setPending] = useState<string | null>(null);
  const go = async () => {
    if (!isEmail(email)) return setError('Enter a valid email address');
    setBusy(true);
    setError('');
    try {
      const r = await account.signIn(email, name);
      if ('pending' in r) setPending(r.email);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="signin-card">
      <span className="muted small">Continue with your email. No password: the same email gets you back to your studios on any device.</span>
      {pending ? (
        <SignInCode email={pending} name={name} onDone={() => setPending(null)} onCancel={() => setPending(null)} />
      ) : (
        <>
          <div className="signin-row">
            <input ref={emailInput} type="email" autoComplete="email" placeholder="you@example.com" value={email} onChange={(e) => setEmail(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && go()} aria-label="Email" autoFocus />
            <input autoComplete="name" placeholder="Your name (optional)" value={name} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && go()} aria-label="Your name" />
          </div>
          <button className="primary big" onClick={go} disabled={busy || !email.trim()} style={{ alignSelf: 'flex-start' }}>
            {busy && <span className="spinner" />} Continue
          </button>
          {error && (
            <div className="alert bad">
              <Icon name="warn" size={16} /> {error}
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------

function Dashboard() {
  const me = account.account!;
  const [studios, setStudios] = useState<StudioSummary[] | null>(null);
  const [created, setCreated] = useState<CreateStudioResponse | null>(null);
  const [loadError, setLoadError] = useState('');
  useTitle('Your studios');
  const load = () =>
    void api
      .myStudios()
      .then((s) => {
        setStudios(s);
        setLoadError('');
      })
      .catch((e) => setLoadError((e as Error).message));
  useEffect(load, [me.id]);
  useInterval(() => document.visibilityState === 'visible' && load(), 15_000);

  const hosted = studios?.filter((s) => s.role !== 'guest') ?? [];
  const guested = studios?.filter((s) => s.role === 'guest') ?? [];
  const takes = hosted.reduce((a, s) => a + s.takeCount, 0);

  return (
    <main className="dash">
      <div className="dash-head">
        <div className="col" style={{ gap: 4 }}>
          <span className="eyebrow">{me.name ? `Hello, ${me.name}` : 'Welcome back'}</span>
          <h1>Your studios</h1>
          {studios && (
            <span className="muted small">
              {hosted.length} studio{hosted.length === 1 ? '' : 's'} · {takes} take{takes === 1 ? '' : 's'} recorded · signed in as {me.email}
            </span>
          )}
        </div>
      </div>

      {created ? (
        <ReadyCard created={created} onDone={() => setCreated(null)} />
      ) : me.canCreateStudios === false ? (
        <RequestAccess />
      ) : (
        <CreateStudio
          onCreated={(c) => {
            setCreated(c);
            load();
          }}
        />
      )}

      <section className="col" style={{ gap: 12 }}>
        {loadError && (
          <div className="alert warn">
            <Icon name="warn" size={16} /> Couldn’t load your studios: {loadError}
          </div>
        )}
        {!studios && !loadError && (
          <div className="card row muted">
            <span className="spinner" /> Loading your studios…
          </div>
        )}
        {studios && !hosted.length && (
          <div className="card empty" style={{ border: '1px solid var(--line)' }}>
            <Icon name="film" size={26} />
            <strong style={{ color: 'var(--text)' }}>No studios yet</strong>
            {me.canCreateStudios === false
              ? 'Studios you’re invited to, and the recordings made in them, will be listed here.'
              : 'Create one above. Studios you host, and the recordings made in them, will be listed here.'}
          </div>
        )}
        <div className="studio-list">
          {hosted.map((s) => (
            <StudioCard key={s.id} s={s} onChange={load} />
          ))}
        </div>
      </section>

      {guested.length > 0 && (
        <section className="col" style={{ gap: 12 }}>
          <div className="section-head">
            <h2>Studios you’ve been a guest in</h2>
          </div>
          <div className="studio-list">
            {guested.map((s) => (
              <StudioCard key={s.id} s={s} onChange={load} />
            ))}
          </div>
        </section>
      )}

      <UploadsPanel />
      <LocalCopies />
    </main>
  );
}

function CreateStudio({ onCreated }: { onCreated: (c: CreateStudioResponse) => void }) {
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const create = async () => {
    setBusy(true);
    setError('');
    try {
      onCreated(await api.createStudio(name.trim() || 'My studio'));
      setName('');
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="card create-card">
      <div className="col" style={{ gap: 2 }}>
        <h2>Create a studio</h2>
        <span className="muted small">A studio is a room you can record in as often as you like. You’ll get links for producers and guests.</span>
      </div>
      <div className="create-row">
        <input placeholder="Studio name, e.g. “Weekly Podcast”" value={name} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && create()} aria-label="Studio name" />
        <button className="primary" onClick={create} disabled={busy}>
          {busy ? <span className="spinner" /> : <Icon name="plus" />} Create studio
        </button>
      </div>
      {error && (
        <div className="alert bad">
          <Icon name="warn" size={16} /> {error}
        </div>
      )}
    </div>
  );
}

/** For people who can't create studios yet: register interest, or see that it's registered. */
function RequestAccess() {
  const me = account.account!;
  const [use, setUse] = useState<AccessUse | null>(null);
  const [size, setSize] = useState<AccessGroupSize | null>(null);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const card = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (location.hash === REGISTER) card.current?.scrollIntoView({ block: 'center' });
  }, []);
  const send = async () => {
    if (!use || !size) return;
    setBusy(true);
    setError('');
    try {
      await account.requestAccess({ use, groupSize: size, note: note.trim() || undefined });
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  if (me.accessRequested) {
    return (
      <div className="card" ref={card}>
        <div className="ready-head">
          <span className="ready-icon">
            <Icon name="check" />
          </span>
          <div className="col grow" style={{ gap: 3 }}>
            <h2>You’re on the list</h2>
            <p className="muted small">
              Thanks for registering interest. We’ll email {me.email} when you can create studios. Meanwhile you can join any studio you’re invited
              to, and it will appear below.
            </p>
          </div>
        </div>
      </div>
    );
  }
  const options = <T extends string>(label: string, all: [T, string][], value: T | null, set: (v: T) => void) => (
    <div className="field">
      {label}
      <div className="option-chips" role="radiogroup" aria-label={label}>
        {all.map(([id, text]) => (
          <button key={id} role="radio" aria-checked={value === id} className={value === id ? 'on' : ''} onClick={() => set(id)}>
            {text}
          </button>
        ))}
      </div>
    </div>
  );
  return (
    <div className="card create-card" ref={card}>
      <div className="col" style={{ gap: 2 }}>
        <span className="eyebrow">Pre-release</span>
        <h2>Register interest in creating studios</h2>
        <span className="muted small">
          We’re inviting a few test users to create studios while Wordstow is in pre-release. Tell us a little about what you’d record. You can
          already join any studio you’re invited to.
        </span>
      </div>
      {options('What will you record?', ACCESS_USES, use, setUse)}
      {options('How many people usually take part, including you?', ACCESS_SIZES, size, setSize)}
      <label className="field">
        Anything else we should know? (optional)
        <textarea rows={3} maxLength={1000} value={note} onChange={(e) => setNote(e.target.value)} placeholder="For example: a weekly interview show with guests in other countries" />
      </label>
      <div className="row wrap" style={{ gap: 12 }}>
        <button className="primary" onClick={send} disabled={busy || !use || !size}>
          {busy && <span className="spinner" />} Register interest
        </button>
        <span className="field-hint">We’ll only use this to choose test users and to contact you about Wordstow.</span>
      </div>
      {error && (
        <div className="alert bad">
          <Icon name="warn" size={16} /> {error}
        </div>
      )}
    </div>
  );
}

function ReadyCard({ created, onDone }: { created: CreateStudioResponse; onDone: () => void }) {
  const origin = location.origin;
  const id = created.studio.id;
  return (
    <div className="card">
      <div className="ready-head">
        <span className="ready-icon">
          <Icon name="check" />
        </span>
        <div className="col grow" style={{ gap: 3 }}>
          <h2>“{created.studio.name}” is ready</h2>
          <p className="muted small">Each link carries its role, so share them carefully. They’re always available from this page and inside the studio.</p>
        </div>
        <button className="ghost sm" onClick={onDone}>
          Done
        </button>
      </div>
      <div className="link-row">
        <div className="role">
          <span className="role-icon"><Icon name="host" size={16} /></span>
          <div>
            <strong>Host</strong>
            <span>Yours. Runs the recording.</span>
          </div>
        </div>
        <CopyField value={`${origin}/s/${id}?k=${created.hostKey}`} label="Host link" />
        <a className="btn primary" href={`/s/${id}?k=${created.hostKey}`}>
          Enter
        </a>
      </div>
      <div className="link-row">
        <div className="role">
          <span className="role-icon"><Icon name="folder" size={16} /></span>
          <div>
            <strong>Producer</strong>
            <span>For collaborators who join and download the files.</span>
          </div>
        </div>
        <CopyField value={`${origin}/s/${id}?k=${created.producerKey}`} label="Producer link" />
        <a className="btn" href={`/s/${id}/files?k=${created.producerKey}`}>
          Files
        </a>
      </div>
      <div className="link-row">
        <div className="role">
          <span className="role-icon"><Icon name="user" size={16} /></span>
          <div>
            <strong>Guest</strong>
            <span>Send to the people you’re recording.</span>
          </div>
        </div>
        <CopyField value={`${origin}/s/${id}?k=${created.guestKey}`} label="Guest link" />
        <span />
      </div>
    </div>
  );
}

function StudioCard({ s, onChange }: { s: StudioSummary; onChange: () => void }) {
  const [confirming, setConfirming] = useState(false);
  const hostKey = s.links?.host ? new URL(s.links.host, location.origin).searchParams.get('k') : null;
  const enter = s.links?.host ?? s.links?.producer ?? s.links?.guest;
  const key = enter ? new URL(enter, location.origin).searchParams.get('k') : null;
  const staff = s.role !== 'guest';
  const files = staff && key ? `/s/${s.id}/files?k=${encodeURIComponent(key)}` : null;
  return (
    <section className="card studio-card">
      <div className="head">
        <span className="studio-icon">
          <Icon name={staff ? 'film' : 'user'} size={18} />
        </span>
        <div className="col grow" style={{ gap: 3, minWidth: 180 }}>
          <div className="row" style={{ gap: 8 }}>
            <h2 className="ellipsis">{s.name}</h2>
            <span className="pill">{roleLabel(s.role)}</span>
          </div>
          <div className="meta">
            <span>{s.takeCount ? `${s.takeCount} take${s.takeCount === 1 ? '' : 's'}` : staff ? 'No takes yet' : 'Guest'}</span>
            {s.totalBytes > 0 && staff && <span>{formatBytes(s.totalBytes)}</span>}
            <span>Active {ago(s.lastActivityAt)}</span>
          </div>
        </div>
        <div className="row wrap" style={{ gap: 6 }}>
          {staff && s.links?.guest && <CopyButton text={location.origin + s.links.guest} label="Copy guest link" />}
          {files && (
            <a className="btn" href={files}>
              <Icon name="folder" size={15} /> Files
            </a>
          )}
          {enter && (
            <a className="btn primary" href={enter}>
              {staff ? 'Enter studio' : 'Rejoin'}
            </a>
          )}
          {hostKey && (
            <Menu down className="ghost icon" title="More" label={<Icon name="more" size={18} />}>
              {(close) => (
                <button className="danger" onClick={() => (close(), setConfirming(true))}>
                  Delete studio…
                  <span className="hint">Removes every take and file in it from Wordstow</span>
                </button>
              )}
            </Menu>
          )}
        </div>
      </div>
      {confirming && hostKey && (
        <ConfirmDialog
          title={`Delete “${s.name}”?`}
          confirmLabel="Delete studio"
          typeToConfirm={s.name}
          onClose={() => setConfirming(false)}
          onConfirm={async () => {
            await api.deleteStudio(s.id, hostKey);
            onChange();
          }}
        >
          <p>
            The studio, {s.takeCount ? `all ${s.takeCount} of its take${s.takeCount === 1 ? '' : 's'} (${formatBytes(s.totalBytes)}) ` : ''}and every link
            to it are removed from Wordstow for good. Anyone still in it is disconnected. This can’t be undone.
          </p>
          <p className="muted small">Copies on participants’ own computers aren’t affected.</p>
        </ConfirmDialog>
      )}
      {s.recentTakes.length > 0 && files && (
        <div className="takes">
          {s.recentTakes.map((t) => (
            <a key={t.id} className="take-row" href={files} style={{ textDecoration: 'none' }} title="Open this studio's files">
              <strong>Take {t.number}</strong>
              <span className="ellipsis">
                {new Date(t.startedAt).toLocaleString(undefined, { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                {t.stoppedAt ? ` · ${formatDuration(t.stoppedAt - t.startedAt)}` : ''} · {t.people} {t.people === 1 ? 'person' : 'people'} · {t.files} file
                {t.files === 1 ? '' : 's'}
              </span>
              <span className="tnum hide-narrow">{formatBytes(t.bytes)}</span>
              {!t.stoppedAt ? (
                <span className="pill rec">
                  <span className="dot pulse" /> Recording
                </span>
              ) : t.complete ? (
                <span className="pill ok">
                  <Icon name="check" size={12} /> Uploaded
                </span>
              ) : t.files ? (
                <span className="pill accent">
                  <Icon name="upload" size={12} /> Uploading
                </span>
              ) : (
                <span className="pill">No files</span>
              )}
            </a>
          ))}
          {s.takeCount > s.recentTakes.length && (
            <a className="take-row" href={files} style={{ textDecoration: 'none', gridTemplateColumns: '1fr' }}>
              <span className="small">All {s.takeCount} takes on the Files page →</span>
            </a>
          )}
        </div>
      )}
    </section>
  );
}

function CopyButton({ text, label }: { text: string; label: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      className="ghost"
      onClick={async () => {
        await navigator.clipboard.writeText(text);
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      }}
    >
      <Icon name={copied ? 'check' : 'link'} size={15} /> {copied ? 'Copied' : label}
    </button>
  );
}

function ago(ms: number) {
  const s = (Date.now() - ms) / 1000;
  if (s < 90) return 'just now';
  if (s < 3600) return `${Math.round(s / 60)} min ago`;
  if (s < 86400) return `${Math.round(s / 3600)} h ago`;
  if (s < 2 * 86400) return 'yesterday';
  return new Date(ms).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: new Date(ms).getFullYear() === new Date().getFullYear() ? undefined : 'numeric' });
}
